import * as React from 'react';
import {
    SaveButton,
    SimpleForm,
    TextInput,
    Toolbar,
    required,
    useNotify,
    useRefresh,
    FileInput,
    FileField,
    SelectInput,
    useDataProvider,
    useGetList,
} from 'react-admin';
import Dialog from '@mui/material/Dialog';
import { config } from '../../config';
import { getJwtHeader } from '../../auth/authProvider';
import { convertFileToBase64 } from '../../data-provider-with-image-convert';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';

const MessageToolbar = () => {
    return (
        <Toolbar>
            <SaveButton label="Küldés" />
        </Toolbar>
    );
};

const UniqueRequestTemplateSelect = () => {
    const dataProvider = useDataProvider();
    const { setValue } = useFormContext();
    const templateId = useWatch({ name: 'templateId' });

    const { data: templates = [], isLoading } = useGetList(
        'unique-request-template',
        {
            pagination: { page: 1, perPage: 10000 },
            sort: { field: 'Name', order: 'ASC' },
            filter: {},
        }
    );

    useEffect(() => {
        if (!templateId) {
            return;
        }

        dataProvider
            .getOne('unique-request-template', { id: templateId })
            .then(({ data }) => {
                setValue('content', data.content ?? '', {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            });
    }, [dataProvider, setValue, templateId]);

    return (
        <SelectInput
            source="templateId"
            label={'Sablon'}
            choices={templates}
            optionText="name"
            fullWidth
            disabled={isLoading}
        />
    );
};

export const UniqueRequestMessageCreateDialog = ({
    uniqueRequestId,
    open,
    setOpen,
}: {
    uniqueRequestId: string;
    open: boolean;
    setOpen: (open: boolean) => void;
}) => {
    const notify = useNotify();
    const refresh = useRefresh();

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            PaperProps={{
                style: { maxWidth: 750 },
            }}
        >
            <SimpleForm
                toolbar={<MessageToolbar />}
                onSubmit={async (values) => {
                    const files = values.attachments ?? [];
                    const attachments = await Promise.all(
                        files
                            .filter((file: any) => file.rawFile instanceof File)
                            .map(async (file: any) => ({
                                name: file.title,
                                url: await convertFileToBase64(file),
                            }))
                    );

                    const response = await fetch(
                        `${config.apiUrl}/unique-request/${uniqueRequestId}/message`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: getJwtHeader(),
                            },
                            body: JSON.stringify({
                                content: values.content,
                                attachments: attachments,
                            }),
                        }
                    );

                    if (!response.ok) {
                        const message = await response.text();
                        notify(message, { type: 'error' });
                        return;
                    }

                    notify('Sikeres üzenet küldés', { type: 'success' });
                    refresh();
                    setOpen(false);
                }}
            >
                <UniqueRequestTemplateSelect />
                <TextInput
                    source="content"
                    label={'Üzenet'}
                    fullWidth
                    multiline
                    validate={required()}
                    minRows={10}
                />
                <FileInput
                    source="attachments"
                    name={'attachments'}
                    multiple
                    label={'Csatolmányok'}
                >
                    <FileField source="src" title="title" />
                </FileInput>
            </SimpleForm>
        </Dialog>
    );
};
